import { useState, type FormEvent, useEffect } from 'react'
import { z } from 'zod'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { Textarea } from '@/shared/ui/textarea'
import { useRegisterForEvent } from '../api/use-applications'
import { useTelegram } from '@/shared/lib/telegram'
import { useUser } from '@/entities/user'

/**
 * Схема валидации формы заявки
 */
const applicationSchema = z.object({
  phone: z.string().max(20, 'Телефон слишком длинный').optional().or(z.literal('')),
  email: z.string().email('Неверный email').optional().or(z.literal('')),
  message: z.string().max(1000, 'Сообщение слишком длинное').optional(),
  guestsCount: z.number().int().min(1).max(20).optional(),
})

interface FormData {
  phone: string
  email?: string
  message?: string
  guestsCount?: number
}

export interface ApplicationFormProps {
  /** ID события */
  eventId: string
  /** Название события */
  eventTitle: string
  /** Максимальное количество гостей (доступные места) */
  maxGuests?: number
  /** Callback при успешной отправке заявки */
  onSuccess?: () => void
  /** Callback при отмене */
  onCancel?: () => void
}

/**
 * Форма заявки на событие
 */
export function ApplicationForm({
  eventId,
  eventTitle,
  maxGuests = 20,
  onSuccess,
  onCancel,
}: ApplicationFormProps) {
  const { hapticFeedback } = useTelegram()
  const registerForEvent = useRegisterForEvent()
  const { data: userResponse } = useUser()
  const user = userResponse?.data

  const [formData, setFormData] = useState<FormData>({
    phone: '',
    email: '',
    message: '',
    guestsCount: 1,
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Предзаполняем данные пользователя
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        phone: prev.phone || user.phone || '',
      }))
    }
  }, [user])

  const validate = (data: FormData): boolean => {
    const newErrors: Record<string, string> = {}
    
    // Если телефона нет в базе, он обязателен
    if (!user?.phone && !data.phone) {
      newErrors.phone = 'Телефон обязателен'
    }

    if (data.guestsCount && data.guestsCount > maxGuests) {
      newErrors.guestsCount = `Максимум мест: ${maxGuests}`
    }

    try {
      applicationSchema.parse(data)
    } catch (error) {
      if (error instanceof z.ZodError) {
        error.errors.forEach((err) => {
          if (err.path[0]) {
            newErrors[err.path[0].toString()] = err.message
          }
        })
      }
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (field: keyof FormData, value: string | number | undefined) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    // Очищаем ошибку при изменении поля
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    
    const data: FormData = {
      ...formData,
      email: formData.email || undefined,
      message: formData.message || undefined,
    }
    
    if (!validate(data)) {
      return
    }

    setIsSubmitting(true)
    try {
      hapticFeedback.impactOccurred('medium')
      await registerForEvent.mutateAsync({
        eventId,
        eventTitle,
        phone: data.phone || undefined,
        email: data.email,
        message: data.message,
        guestsCount: data.guestsCount,
      })
      hapticFeedback.notificationOccurred('success')
      onSuccess?.()
    } catch (error) {
      hapticFeedback.notificationOccurred('error')
      console.error('Ошибка при отправке заявки:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const hasPhone = !!user?.phone

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      {/* Телефон (показываем только если его нет в базе) */}
      {!hasPhone && (
        <div className="space-y-2">
          <label htmlFor="phone" className="block text-sm font-medium text-ink dark:text-white">
            Телефон <span className="text-destructive">*</span>
          </label>
          <Input
            id="phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            placeholder="+7 (900) 123-45-67"
            state={errors.phone ? 'error' : 'default'}
            errorMessage={errors.phone}
            disabled={isSubmitting}
          />
        </div>
      )}

      {/* Количество гостей (всегда показываем) */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label htmlFor="guestsCount" className="block text-sm font-medium text-ink dark:text-white">
            Количество гостей
          </label>
          {maxGuests < 20 && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Осталось мест: {maxGuests}
            </span>
          )}
        </div>
        <Input
          id="guestsCount"
          type="number"
          min={1}
          max={maxGuests}
          value={formData.guestsCount || ''}
          onChange={(e) => {
            const val = e.target.value ? Number(e.target.value) : undefined
            // Ограничиваем максимальным количеством мест
            const limitedVal = val ? Math.min(val, maxGuests) : undefined
            handleChange('guestsCount', limitedVal)
          }}
          placeholder="1"
          state={errors.guestsCount ? 'error' : 'default'}
          errorMessage={errors.guestsCount}
          disabled={isSubmitting}
        />
      </div>

      {/* Сообщение / Комментарий (всегда показываем) */}
      <div className="space-y-2">
        <label htmlFor="message" className="block text-sm font-medium text-ink dark:text-white">
          {hasPhone ? 'Комментарий' : 'Сообщение (необязательно)'}
        </label>
        <Textarea
          id="message"
          value={formData.message || ''}
          onChange={(e) => handleChange('message', e.target.value)}
          placeholder="Дополнительная информация..."
          rows={4}
          state={errors.message ? 'error' : 'default'}
          errorMessage={errors.message}
          disabled={isSubmitting}
        />
      </div>

      {/* Кнопки */}
      <div className="flex gap-3 pt-4">
        {onCancel && (
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isSubmitting}
            className="flex-1 h-14 rounded-2xl"
          >
            Отмена
          </Button>
        )}
        <Button
          type="submit"
          disabled={isSubmitting}
          className="flex-1 h-14 rounded-2xl"
        >
          {isSubmitting ? 'Отправка...' : 'Записаться'}
        </Button>
      </div>
    </form>
  )
}
